import _ from "lodash";
import React, { useMemo, useCallback } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { graphql, compose } from "lib/apolloClient";

// hooks
import { useI18n } from "Hooks";

// design system
import { Button, Tooltip } from "@toddle-edu/ds-web";

// components
import FeedListv2 from "AppComponents/FeedListv2";
import withLoader from "UIComponents/GqlWithLoader";
import GradeScaleDatagrid from "Administrator/commonComponents/GradingSetupV2/components/GradeScaleDatagrid";

// constants
import {
  GRADE_SCALE_TOOL,
  ELEMENT_TYPE_UNIT_PLAN,
} from "Constants/stringConstants";

// modules
import { getIsStaff } from "modules/CommonSelectors";
import {
  createIbPypElementRating,
  deletePypElementRating,
} from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { getGradeScaleDetailsQuery } from "../../modules/AssessmentGradeScaleToolQueries";

// utils
import { calculateGradeScaleRatingFromOtherTool } from "Utils/AssessmentToolHelpers";
import { getScoreValueElementRatings } from "ClassRoom/modules/utils";

// styles
import classes from "./AssessmentGradeScaleToolView.scss";
import {
  getIsMediaManagerRoute,
  setElementRatingMutationInProgress,
} from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomModule";

const styles = {
  tooltipOverlayStyle: { zIndex: 10000 },
  tooltipOverlayInnerStyle: { maxWidth: "280px" },
  tableContainerStyle: { width: "100%" },
  dataGridContainerStyle: {
    overflow: "auto",
  },
};

const AssessmentGradeScaleToolView = props => {
  const {
    isStaff,
    ibPYPElementRatings,
    assessmentTool,
    createIbPypElementRating,
    deletePypElementRating,
    sourceFrom,
    studentDetails,
    parentId,
    academicTermId,
    isAutoSaveDisabled,
    setIBPYPElementRatingsLocal,
    ibPYPElementRatingsLocal,
    isEvaluated,
    isEvaluationEnable,
    toolResponsesData,
    assessmentTools,
    gradeScaleEntityDetails,
    handleRefetchStudentRatings,
  } = props;

  // hooks
  const { t } = useI18n();

  const { gradeScale, gradeScaleValues } = assessmentTool || {};
  const { gradingType } = gradeScale || {};
  const isAlphaGradeScale = gradingType === "ALPHA";

  let _ibPYPElementRatings = ibPYPElementRatings;

  if (isAutoSaveDisabled && _.isFunction(setIBPYPElementRatingsLocal)) {
    _ibPYPElementRatings = ibPYPElementRatingsLocal?.[parentId];
  }
  const scoreValue = getScoreValueElementRatings({
    ibPYPElementRatings: _ibPYPElementRatings,
  });

  const calculatedRating = useMemo(() => {
    return calculateGradeScaleRatingFromOtherTool({
      assessmentTools,
      ibPYPElementRatings: _ibPYPElementRatings,
      toolResponses: toolResponsesData,
      score: scoreValue,
    });
  }, [toolResponsesData, assessmentTools, _ibPYPElementRatings, scoreValue]);

  const selectedRating = _.find(
    _ibPYPElementRatings,
    ({ ibPYPElementType }) => ibPYPElementType === GRADE_SCALE_TOOL
  );

  const academicCriteriaValueId = _.get(
    selectedRating,
    "academicCriteriaValue.id"
  );

  const feedData = useMemo(() => {
    return _.chain(gradeScaleValues)
      .orderBy("displaySequence", "asc")
      .map(value => {
        const { id: key, label, abbreviation, description } = value;

        return {
          id: key,
          abbreviation,
          value: label,
          description,
          disableRowClick: true,
        };
      })
      .value();
  }, [gradeScaleValues]);

  const columnList = useMemo(() => {
    const hasValue = _.some(feedData, item => !_.isEmpty(item?.value));
    const hasDescription = _.some(
      feedData,
      item => !_.isEmpty(item?.description)
    );

    // Determine abbreviation column width based on available data
    let abbreviationWidth = "88px";
    if (!hasValue && !hasDescription) {
      abbreviationWidth = "300px";
    }

    const columns = [
      {
        id: "abbreviation",
        label: isAlphaGradeScale
          ? t("common:grade")
          : t("common:numeric_value"),
        type: "CUSTOM",
        style: { minWidth: abbreviationWidth },
      },
    ];

    if (hasValue) {
      const valueWidth = !hasDescription ? "300px" : "180px";

      columns.push({
        id: "value",
        label: t("worksheet:label"),
        type: "CUSTOM",
        style: { minWidth: valueWidth },
      });
    }

    if (hasDescription) {
      const descriptionWidth = hasValue ? "180px" : "300px";

      columns.push({
        id: "description",
        label: t("common:description"),
        type: "CUSTOM",
        style: { minWidth: descriptionWidth },
      });
    }

    return columns;
  }, [feedData, isAlphaGradeScale, t]);

  // event handlers
  const onChangeGradeScaleValue = useCallback(
    async ({ key }) => {
      const commonArgs = {
        isAutoSaveDisabled,
        academicTermId,
        sourceFrom,
        setIBPYPElementRatingsLocal,
        ibPYPElementRatingsLocal,
        parentId,
        parentType: "STUDENT_ASSIGNMENT",
        setId: assessmentTool?.gradeScale?.id,
        id: assessmentTool?.id,
        type: GRADE_SCALE_TOOL,
        studentId: studentDetails?.id,
      };

      // If key is null, delete the rating (unselect)
      if (key === null) {
        await deletePypElementRating(commonArgs);
        if (sourceFrom === "GRADEBOOK" && handleRefetchStudentRatings) {
          handleRefetchStudentRatings();
        }
        return;
      }

      // Otherwise, create/update the rating (select)
      const gradeScaleValue = _.find(
        assessmentTool?.gradeScaleValues,
        ({ id }) => id === key
      );

      const academicCriteriaValue = {
        color: null,
        description: null,
        ...(_.pick(gradeScaleValue, ["id", "label", "abbreviation"]) || {}),
      };

      const criteriaSet = {
        id: commonArgs.setId,
        label: gradeScale?.displayName || "",
        criteriaType: "GRADE_SCALE",
        isArchived: false,
        subType: null,
      };

      // when overriding the rating add is_overridden to metadata
      let metadata = null;
      if (calculatedRating && calculatedRating?.id !== key) {
        metadata = { is_overridden: true };
      }

      await createIbPypElementRating({
        ...commonArgs,
        criteriaValue: key,
        isPublished: isEvaluated,
        score: gradeScaleValue?.abbreviation,
        academicCriteriaValue,
        criteriaSet,
        metadata,
        getIsMediaManagerRoute,
        setElementRatingMutationInProgress,
      });

      if (sourceFrom === "GRADEBOOK" && handleRefetchStudentRatings) {
        handleRefetchStudentRatings();
      }
    },
    [
      isAutoSaveDisabled,
      academicTermId,
      sourceFrom,
      setIBPYPElementRatingsLocal,
      ibPYPElementRatingsLocal,
      parentId,
      assessmentTool,
      studentDetails,
      deletePypElementRating,
      gradeScale,
      createIbPypElementRating,
      isEvaluated,
      calculatedRating,
      handleRefetchStudentRatings,
    ]
  );

  const getCustomComponent = useCallback(
    ({ columnData, column }) => {
      const isLastColumn = column?.id === _.get(_.last(columnList), "id");

      return (
        <Tooltip
          tooltip={columnData}
          placement={"left"}
          visible={_.size(columnData) > 20}
          overlayInnerStyle={styles.tooltipOverlayInnerStyle}
          overlayStyle={styles.tooltipOverlayStyle}
        >
          <span
            className={classNames("text-body", classes.rowCell, {
              [classes.lastColumnRowCell]: isLastColumn,
            })}
          >
            {columnData || "-"}
          </span>
        </Tooltip>
      );
    },
    [columnList]
  );

  // render methods

  const renderOverridenMessage = ({ previousRating }) => {
    const previousScore = previousRating?.abbreviation || "-";
    const newScore =
      _.find(gradeScaleValues, { id: academicCriteriaValueId })?.abbreviation ||
      "-";

    return (
      <div className={classNames("text-body-s", classes.overrideMessage)}>
        <Trans
          i18nKey="classRoom:calculated_grade_override_message"
          values={{ previousScore, newScore }}
        >
          <span>
            Calculated grade has been changed from &quot;{{ previousScore }}
            &quot; to &quot;{{ newScore }}&quot;.
          </span>
          <Button
            variant={"progressive"}
            size={"small"}
            type={"inline"}
            onClick={() => onChangeGradeScaleValue({ key: previousRating?.id })}
            disabled={!isEvaluationEnable}
          >
            Click to reset
          </Button>
          <span>to calculated grade.</span>
        </Trans>
      </div>
    );
  };

  const renderChip = useCallback(
    ({ id, abbreviation, isEmptyOption = false }) => {
      const isSelected = isEmptyOption
        ? _.isEmpty(selectedRating)
        : _.isEqual(academicCriteriaValueId, id);

      const isCalculatedRating =
        calculatedRating && _.isEqual(_.get(calculatedRating, "id"), id);

      const isCalculatedAndSelected = isCalculatedRating && isSelected;

      const handleChipClick = () => {
        if (isEvaluationEnable && !isSelected) {
          onChangeGradeScaleValue({ key: isEmptyOption ? null : id });
        }
      };

      const handleKeyDown = event => {
        if (
          isEvaluationEnable &&
          !isSelected &&
          ["Enter", " "].includes(event.key)
        ) {
          event.preventDefault();
          onChangeGradeScaleValue({ key: isEmptyOption ? null : id });
        }
      };

      const chipClasses = classNames(classes.chip, {
        [classes.chipSelected]: isSelected && !isCalculatedAndSelected,
        [classes.chipDisabled]: !isEvaluationEnable,
        [classes.chipCalculated]: isCalculatedRating && !isSelected,
        [classes.chipCalculatedSelected]: isCalculatedAndSelected,
      });

      const chipElement = (
        <div
          key={isEmptyOption ? "empty-option" : id}
          className={chipClasses}
          onClick={handleChipClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={isEvaluationEnable ? 0 : -1}
          aria-pressed={isSelected}
          aria-disabled={!isEvaluationEnable}
        >
          <span className={classNames("text-body", classes.chipText)}>
            {abbreviation}
          </span>
        </div>
      );

      // Show tooltip for calculated grade when it's not selected
      if (isCalculatedRating && !isSelected) {
        return (
          <Tooltip
            tooltip={t("common:calculated_grade")}
            placement="top"
            overlayStyle={styles.tooltipOverlayStyle}
            overlayInnerStyle={styles.tooltipOverlayInnerStyle}
          >
            {chipElement}
          </Tooltip>
        );
      }

      return chipElement;
    },
    [
      t,
      selectedRating,
      academicCriteriaValueId,
      calculatedRating,
      isEvaluationEnable,
      onChangeGradeScaleValue,
    ]
  );

  const renderStaffView = () => {
    // If sourceFrom is UNIT_PLAN, render GradeScaleDatagrid
    if (sourceFrom === ELEMENT_TYPE_UNIT_PLAN) {
      if (!gradeScaleEntityDetails) return null;

      const { gradeScale, gradeScaleValues, gradeValueCalculateTypeDetail } =
        gradeScaleEntityDetails || {};

      const { type: gradeScaleType, gradingType } = gradeScale || {};

      const gradeValueMethod = _.get(
        gradeValueCalculateTypeDetail,
        "[0].gradeValueCalculateType"
      );

      return (
        <div className={classes.unitPlanViewContainer}>
          <div className={classNames(classes.gradeScaleLabel, "text-label")}>
            {t("academicSetup:grade_scale")}
          </div>

          <GradeScaleDatagrid
            gradeScaleType={gradeScaleType}
            gradeScaleValuesData={gradeScaleValues}
            gradeValueMethod={gradeValueMethod}
            gradeType={gradingType}
            isEditMode={false}
            showActions={false}
            isMasteryLevelEnabled={false}
            isUseForCreditGPACalculation={false}
            containerStyle={styles.dataGridContainerStyle}
          />
        </div>
      );
    }

    // Default staff view for other cases
    const isOverriden =
      calculatedRating &&
      !_.isEqual(_.get(calculatedRating, "id"), academicCriteriaValueId);

    const allChips = [
      { id: null, abbreviation: "-", isEmptyOption: true },
      ..._.map(
        _.orderBy(gradeScaleValues, "displaySequence", "asc"),
        value => ({
          id: _.get(value, "id"),
          abbreviation: _.get(value, "abbreviation"),
          isEmptyOption: false,
        })
      ),
    ];

    return (
      <div className={classes.staffViewContainer}>
        <div className={classes.chipsContainer}>
          {_.map(allChips, renderChip)}
        </div>

        {isOverriden
          ? renderOverridenMessage({ previousRating: calculatedRating })
          : null}
      </div>
    );
  };

  const renderDefaultView = () => {
    // do not render if there's only abbreviation
    if (_.size(columnList) === 1) return null;

    return (
      <div className={classes.studentViewContainer}>
        <FeedListv2
          columnList={columnList}
          feedData={feedData}
          getCustomComponent={getCustomComponent}
          containerStyle={styles.tableContainerStyle}
          headerTheme="subtle"
          dsVersion="2.0"
          showBorder={false}
          isData={true}
          showRowBackgroundOnHover={false}
        />
      </div>
    );
  };

  if (isStaff) {
    return renderStaffView();
  } else {
    return renderDefaultView();
  }
};

const mapStateToProps = state => {
  const isStaff = getIsStaff(state);

  return { isStaff, isData: true, isLoading: false };
};

const mapActionCreators = {
  createIbPypElementRating,
  deletePypElementRating,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getGradeScaleDetailsQuery, {
    name: "getGradeScaleDetails",
    skip: ({ sourceFrom, assessmentTool }) =>
      sourceFrom !== ELEMENT_TYPE_UNIT_PLAN || !assessmentTool?.id,
    options: ({ assessmentTool }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: { id: assessmentTool?.id },
      };
    },
    props({ getGradeScaleDetails: { node, networkStatus } }) {
      return {
        gradeScaleEntityDetails: node,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], networkStatus),
      };
    },
  }),
  withLoader
)(AssessmentGradeScaleToolView);
